import { TypeObject } from '../typings';

const MEDIA_TYPE_PHOTOARCHIVE = 1;
const MEDIA_TYPE_PHOTOARCHIVEPAID = 2;
const MEDIA_TYPE_ONLINEHISTORY = 4;
const MEDIA_TYPE_AVIMAGE = 8;
const MEDIA_TYPE_VIDEO = 16;
const MEDIA_TYPE_VIDEOPAID = 32;
const MEDIA_TYPE_VIDEOTHUMBNAIL = 64;
const MEDIA_TYPE_ALBUM = 128;
const MEDIA_TYPE_POSTERBLUR = 256;

export const PICTURE_SIZE_720P = '720p';
export const PICTURE_SIZE_1080P = '1080p';
export const PICTURE_SIZE_2160P = '2160p';

export class Photo {
    public readonly id: number;
    private date: number;
    public readonly requireLogon: boolean;
    private filename: string;
    private availableBit: number;
    private mediaTypeID: number | null;

    constructor(id: number, date: number, requireLogon: boolean, filename: string, availableBit: number, mediaTypeID: number | null) {
        this.id = id;
        this.date = date;
        this.requireLogon = requireLogon;
        this.filename = filename;
        this.availableBit = availableBit;
        this.mediaTypeID = mediaTypeID;
    }

    getImagePath(): string {
        return this.filename;
    }

    getAlbumId(): string {
        return this.date.toString();
    }
}

export class PhotoArchive {
    private photos: Photo[];
    private idx: number;
    private pathHD: string[];
    private prevPhoto: Photo;

    constructor(pathHD: string[], photos: Photo[]) {
        this.pathHD = pathHD;
        this.photos = photos.filter((photo: Photo) => {
            return photo.requireLogon === false;
        });
        this.idx = 0;
    }

    getPath(): TypeObject {
        return {
            [PICTURE_SIZE_720P]: this.pathHD[0],
            [PICTURE_SIZE_1080P]: this.pathHD[1],
            [PICTURE_SIZE_2160P]: this.pathHD[2],
        };
    }

    isLastPhoto(): boolean {
        return this.idx === this.photos.length - 1;
    }

    isFirstPhoto(): boolean {
        return this.idx === 0;
    }

    isOtherAlbum(photo: Photo): boolean {
        return this.prevPhoto.getAlbumId() !== photo.getAlbumId();
    }

    setPictureId(id: number) {
        const idx = this.photos.findIndex((item: Photo) => {
            return item.id === id;
        });

        if (idx > -1) {
            this.setIdx(idx);
        } else {
            throw new RangeError('No such picture id');
        }
    }

    setIdx(idx: number) {
        this.idx = idx;
        this.prevPhoto = this.photos[this.idx];

        this.preloadImage(this.idx + 1);
        this.preloadImage(this.idx + 2);
        this.preloadImage(this.idx - 1);
    }

    current(): Photo {
        const p = this.photos[this.idx];

        if (!p) {
            throw new RangeError();
        }

        return p;
    }

    next(): Photo {
        this.prevPhoto = this.current();

        const n = this.photos[++this.idx];

        if (!n) {
            this.idx = 0;
            return this.photos[this.idx];
        }
        this.preloadImage(this.idx + 1);
        this.preloadImage(this.idx + 2);
        return n;
    }

    private preloadImage(idx: number) {
        const photo = this.photos[idx];
        if (photo) {
            const image = new Image();
            image.loading = 'eager';
            image.src = `${this.getPath()[PICTURE_SIZE_720P]}${photo.getImagePath()}`;
        }
    }

    prev(): Photo {
        this.prevPhoto = this.current();
        const p = this.photos[--this.idx];

        if (!p) {
            this.idx = this.photos.length - 1;
            return this.photos[this.idx];
        }

        this.preloadImage(this.idx - 1);
        this.preloadImage(this.idx - 2);
        return p;
    }
}
